import React, { useState, useEffect, useCallback, useRef } from "react";
import styles from "../../../styles/about/components/AboutBriefHistory.module.css";

import img1  from "../../../assets/images/image1.png";
import img2  from "../../../assets/images/image2.png";
import img3  from "../../../assets/images/image3.png";
import img4  from "../../../assets/images/image4.png";
import img5  from "../../../assets/images/image5.png";
import img6  from "../../../assets/images/image6.png";
import img7  from "../../../assets/images/image8.png";
import img8  from "../../../assets/images/image9.png";
import img9  from "../../../assets/images/image10.jpg";
import img10 from "../../../assets/images/image11.jpg";
import founderImage from "../../../assets/images/father.png";

import { FaQuoteLeft, FaPlay, FaPause, FaChevronLeft, FaChevronRight } from "react-icons/fa";
import { MdVerified } from "react-icons/md";

function useInView(threshold = 0.12) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setInView(true); observer.disconnect(); } },
      { threshold }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [threshold]);
  return [ref, inView];
}

const SLIDES = [
  { image: img1,  caption: "Church Worship Gathering" },
  { image: img2,  caption: "Ministry Outreach Program" },
  { image: img3,  caption: "Community Service Event" },
  { image: img4,  caption: "Youth Ministry Activities" },
  { image: img5,  caption: "Church Building Progress" },
  { image: img6,  caption: "Leadership Conference" },
  { image: img7,  caption: "Prayer Meeting" },
  { image: img8,  caption: "Mission Trip" },
  { image: img9,  caption: "Church Anniversary Celebration" },
  { image: img10, caption: "Baptism Service" },
];

const SLIDE_DURATION = 5000;

export default function AboutBriefHistory() {
  const [activeTab, setActiveTab] = useState("founder");
  const [slide, setSlide] = useState(0);
  const [playing, setPlaying] = useState(false);
  const intervalRef = useRef(null);

  const [sectionRef, sectionInView] = useInView(0.06);

  const stopPlay = useCallback(() => {
    clearInterval(intervalRef.current);
    intervalRef.current = null;
  }, []);

  const startPlay = useCallback(() => {
    stopPlay();
    intervalRef.current = setInterval(() => {
      setSlide((s) => (s + 1) % SLIDES.length);
    }, SLIDE_DURATION);
  }, [stopPlay]);

  useEffect(() => {
    if (activeTab === "history" && playing) startPlay();
    else stopPlay();
    return stopPlay;
  }, [activeTab, playing, startPlay, stopPlay]);

  useEffect(() => {
    if (activeTab === "history") setPlaying(true);
    else { setPlaying(false); setSlide(0); }
  }, [activeTab]);

  const prev = () => { stopPlay(); setPlaying(false); setSlide((s) => (s - 1 + SLIDES.length) % SLIDES.length); };
  const next = () => { stopPlay(); setPlaying(false); setSlide((s) => (s + 1) % SLIDES.length); };
  const toggle = () => setPlaying((p) => !p);

  return (
    <section
      ref={sectionRef}
      className={`${styles.section} ${sectionInView ? styles.visible : ""}`}
    >
      <div className={styles.blob1} />
      <div className={styles.blob2} />

      <div className={styles.container}>

        {/* Section Header */}
        <div className={styles.sectionHeader}>
          <div className={styles.eyebrow}>Our Story</div>
          <h2 className={styles.sectionTitle}>
            Rooted in <span className={styles.titleHighlight}>Faith & Purpose</span>
          </h2>
          <div className={styles.titleUnderline} />
        </div>

        {/* Tabs */}
        <div className={styles.tabRow}>
          <button
            className={`${styles.tab} ${activeTab === "founder" ? styles.tabActive : ""}`}
            onClick={() => setActiveTab("founder")}
          >
            Our Founder
          </button>
          <button
            className={`${styles.tab} ${activeTab === "history" ? styles.tabActive : ""}`}
            onClick={() => setActiveTab("history")}
          >
            Our Journey
          </button>
        </div>

        {/* ── FOUNDER TAB ── */}
        {activeTab === "founder" && (
          <div className={styles.founderGrid}>
            {/* Image column */}
            <div className={styles.founderImageWrap}>
              <img src={founderImage} alt="Apostle Sebagabo Christophe" className={styles.founderImage} />
              <div className={styles.founderImageOverlay} />
              <span className={styles.founderBadge}>
                <MdVerified className={styles.founderBadgeIcon} />
                Senior Pastor & Founder
              </span>
            </div>

            {/* Content column */}
            <div className={styles.founderContent}>
              <span className={styles.founderEyebrow}>Visionary Leader</span>
              <h3 className={styles.founderName}>
                Apostle Sebagabo Christophe
              </h3>
              <div className={styles.founderDivider} />

              <blockquote className={styles.founderQuote}>
                <FaQuoteLeft className={styles.quoteIcon} />
                <p>
                  Please allow me to personally welcome you to the Calvary Wide
                  Fellowship Ministries website. We are delighted that you're
                  here. This platform is designed to introduce you to the heart
                  of our ministry and provide resources to support your
                  spiritual growth and wholeness in Christ.
                </p>
                <cite>— Apostle Sebagabo Christophe</cite>
              </blockquote>

              <p className={styles.founderBio}>
                Apostle Sebagabo Christophe is the visionary founder and Senior
                Pastor of Calvary Wide Fellowship Ministries. Called by God to
                establish a ministry rooted in the Word, intercession, and
                discipleship, he has led CWFM from its founding with a heart
                for revival, transformation, and raising a generation of
                Kingdom champions across Rwanda and beyond.
              </p>

              <div className={styles.founderStats}>
                <div className={styles.founderStat}>
                  <span className={styles.statNum}>10K+</span>
                  <span className={styles.statLabel}>Lives Touched</span>
                </div>
                <div className={styles.founderStat}>
                  <span className={styles.statNum}>20+</span>
                  <span className={styles.statLabel}>Years in Ministry</span>
                </div>
                <div className={styles.founderStat}>
                  <span className={styles.statNum}>9+</span>
                  <span className={styles.statLabel}>Parishes</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ── HISTORY / JOURNEY TAB ── */}
        {activeTab === "history" && (
          <div className={styles.slideshowWrap}>
            <div className={styles.slideshow}>
              {SLIDES.map((s, i) => (
                <div
                  key={i}
                  className={`${styles.slide} ${i === slide ? styles.slideActive : ""}`}
                >
                  <img src={s.image} alt={s.caption} className={styles.slideImg} />
                  <div className={styles.slideOverlay} />
                  <div className={styles.slideCaption}>{s.caption}</div>
                </div>
              ))}

              {/* Controls */}
              <button className={`${styles.slideBtn} ${styles.slidePrev}`} onClick={prev} aria-label="Previous">
                <FaChevronLeft />
              </button>
              <button className={`${styles.slideBtn} ${styles.slideNext}`} onClick={next} aria-label="Next">
                <FaChevronRight />
              </button>
              <button className={styles.playBtn} onClick={toggle} aria-label={playing ? "Pause" : "Play"}>
                {playing ? <FaPause /> : <FaPlay />}
              </button>

              {/* Dots */}
              <div className={styles.dots}>
                {SLIDES.map((_, i) => (
                  <button
                    key={i}
                    className={`${styles.dot} ${i === slide ? styles.dotActive : ""}`}
                    onClick={() => { stopPlay(); setPlaying(false); setSlide(i); }}
                    aria-label={`Go to slide ${i + 1}`}
                  />
                ))}
              </div>

              {/* Progress bar */}
              <div className={styles.progressBar}>
                <div
                  className={styles.progressFill}
                  style={{ width: `${((slide + 1) / SLIDES.length) * 100}%` }}
                />
              </div>
            </div>

            <p className={styles.slideshowNote}>
              A glimpse of God's faithfulness through the years. Click play to
              journey through our story.
            </p>
          </div>
        )}

      </div>
    </section>
  );
}
